import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";

const authHeaders = (authToken) => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Token ${authToken}`,
});

const normalizeListResponse = (data) => {
  const rows =
    (Array.isArray(data?.results) && data.results) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(data?.records) && data.records) ||
    (Array.isArray(data?.response?.results) && data.response.results) ||
    (Array.isArray(data?.response?.data) && data.response.data) ||
    (Array.isArray(data?.response?.records) && data.response.records) ||
    (Array.isArray(data?.response) && data.response) ||
    (Array.isArray(data?.user_list) && data.user_list) ||
    (Array.isArray(data) ? data : []);

  return {
    rows: Array.isArray(rows) ? rows : [],
    pagination: {
      page_count: data?.page_count || 1,
      total_count: data?.total_count || rows?.length || 0,
      current_page_count:
        data?.current_page_count || data?.total_count || rows?.length || 0,
      current_page: data?.current_page || 1,
      page_size: data?.page_size || rows?.length || 0,
    },
  };
};

export const inventoryApi = {
  async getWarehouseMaster(
    authToken,
    orgToken,
    { page = 1, pageSize = 100, search = "" } = {},
  ) {
    const response = await axios.get(API_ENDPOINT.get_warehouse_master, {
      headers: authHeaders(authToken),
      params: {
        organization_token: orgToken,
        page,
        page_size: pageSize,
        search,
      },
    });
    return normalizeListResponse(response.data);
  },

  async saveWarehouse(authToken, payload) {
    const response = await axios.post(API_ENDPOINT.warehouse_master, payload, {
      headers: authHeaders(authToken),
    });
    return response.data;
  },

  async getInventoryMaster(
    authToken,
    orgToken,
    page = 1,
    pageSize = 100,
    search = "",
  ) {
    const response = await axios.get(API_ENDPOINT.get_inventory_master, {
      headers: authHeaders(authToken),
      params: {
        organization_token: orgToken,
        page,
        page_size: pageSize,
        search,
      },
    });
    return normalizeListResponse(response.data);
  },

  async getApprovedGrnItemsForInventory(authToken, payload) {
    const response = await axios.post(
      API_ENDPOINT.approved_grn_items_for_inventory,
      payload,
      { headers: authHeaders(authToken) },
    );
    return response.data?.response || response.data?.data || response.data;
  },

  async saveInventory(authToken, payload) {
    const response = await axios.post(API_ENDPOINT.inventory_master, payload, {
      headers: authHeaders(authToken),
    });
    return response.data;
  },

  async getItemMaster(authToken, orgToken) {
    const response = await axios.post(
      API_ENDPOINT.view_all_item,
      { org_token: orgToken },
      { headers: authHeaders(authToken) },
    );
    return normalizeListResponse(response.data);
  },

  async getGrns(authToken, payload) {
    const response = await axios.post(API_ENDPOINT.inventory_grns, payload, {
      headers: authHeaders(authToken),
    });
    return normalizeListResponse(response.data);
  },

  async approveGrn(authToken, payload) {
    const response = await axios.post(API_ENDPOINT.approve_grn, payload, {
      headers: authHeaders(authToken),
    });
    return response.data;
  },

  async getDepartments(authToken, orgToken) {
    const response = await axios.post(
      API_ENDPOINT.get_Department,
      { user_token: orgToken },
      { headers: authHeaders(authToken) },
    );
    return normalizeListResponse(response.data);
  },

  async getCostCentres(authToken, buyerToken) {
    const response = await axios.post(
      API_ENDPOINT.cost_center_list,
      {
        buyer_token: buyerToken,
        page: 1,
        sort_by: "costcenter_code",
        order: "asc",
      },
      { headers: authHeaders(authToken) },
    );
    return normalizeListResponse(response.data);
  },

  async getGlCodes(authToken, buyerToken) {
    const response = await axios.post(
      API_ENDPOINT.gl_code_list,
      {
        buyer_token: buyerToken,
      },
      { headers: authHeaders(authToken) },
    );
    return normalizeListResponse(response.data);
  },

  async getUsers(authToken, orgToken) {
    const response = await axios.post(
      API_ENDPOINT.user_list,
      { user_token: orgToken },
      { headers: authHeaders(authToken) },
    );
    return normalizeListResponse(response.data);
  },

  async getOnlyUsers(authToken, orgToken) {
    const response = await axios.post(
      API_ENDPOINT.only_users,
      { org_token: orgToken },
      { headers: authHeaders(authToken) },
    );
    return normalizeListResponse(response.data);
  },

  async getBuyers(authToken, orgToken) {
    const response = await axios.post(
      API_ENDPOINT.getBuyerList,
      { org_token: orgToken },
      {
        headers: {
          ...authHeaders(authToken),
          Authorization: `Bearer ${authToken}`,
        },
      },
    );
    return normalizeListResponse(response.data);
  },

  async getProjects(authToken, buyerToken) {
    const response = await axios.post(
      API_ENDPOINT.projects_list,
      {
        buyer_token: buyerToken,
        page: 1,
        sort_by: "project_code",
        order: "asc",
      },
      { headers: authHeaders(authToken) },
    );
    return normalizeListResponse(response.data);
  },

  async createMaterialIssue(authToken, payload) {
    const response = await axios.post(API_ENDPOINT.material_issue, payload, {
      headers: authHeaders(authToken),
    });
    return response.data;
  },

  async getMaterialIssues(
    authToken,
    orgToken,
    { page = 1, pageSize = 100, search = "" } = {},
  ) {
    const response = await axios.get(API_ENDPOINT.get_material_issued, {
      headers: authHeaders(authToken),
      params: {
        organization_token: orgToken,
        page,
        page_size: pageSize,
        search,
      },
    });
    return normalizeListResponse(response.data);
  },

  async createStockMovement(authToken, payload) {
    const response = await axios.post(API_ENDPOINT.stock_movement, payload, {
      headers: authHeaders(authToken),
    });
    return response.data;
  },

  async createStockTransfer(authToken, payload) {
    const response = await axios.post(API_ENDPOINT.stock_transfer, payload, {
      headers: authHeaders(authToken),
    });
    return response.data;
  },

  async getStockLedger(
    authToken,
    orgToken,
    { page = 1, pageSize = 100, search = "" } = {},
  ) {
    const response = await axios.get(API_ENDPOINT.stock_ledger, {
      headers: authHeaders(authToken),
      params: {
        organization_token: orgToken,
        page,
        page_size: pageSize,
        search,
      },
    });
    return normalizeListResponse(response.data);
  },

  async getConsumptionReport(
    authToken,
    orgToken,
    dimension,
    { page = 1, pageSize = 100, search = "" } = {},
  ) {
    const response = await axios.get(API_ENDPOINT.consumption_report, {
      headers: authHeaders(authToken),
      params: {
        organization_token: orgToken,
        dimension,
        page,
        page_size: pageSize,
        search,
      },
    });
    return normalizeListResponse(response.data);
  },
};
